// ============================================================
//  Seed-Loader: public/seed/dataset.json (aus scripts/seed-to-app.mjs) → Dataset.
//  Die Datei enthält ROHDATEN. Klassifikation, Personen und Maßnahmen entstehen
//  hier über dieselbe Funktion wie beim Generator (src/domain/build.ts).
// ============================================================
import type { Account, CompositeRole, Person, PersonType, SingleRole, TraceState, LeverCluster } from "../domain/types";
import { classifyValues } from "../domain/classification";
import { buildDataset } from "../domain/build";
import { DEFAULT_RATIO, weight } from "../domain/rules";
import { assignedLandscapes, landscapes } from "./landscapes";
import { baseEntitlements, snapshotsFor, type Dataset } from "./generator";

export interface SeedJson {
  meta: { source: string; seed: number; generatedAt: string; ruleset: string; persons: number; accounts: number };
  systems: { sid: string; landscape: string; metric: string; text: string }[];
  singleRoles: { id: string; name: string; sid: string; template: string; objectsTotal: number; removable: boolean; values: [string, string | null, string | null][] }[];
  compositeRoles: { id: string; name: string; sid: string; template: string; singles: string[] }[];
  /** [pernr, jobFamily, orgUnit, first, last, email, department, location] */
  persons: [string, string, string, string, string, string, string, string][];
  /** [pernr, sid, mandt, bname, email, lastLogon, locked, dev, composites, singles, traces, licType, ustyp, su01Function?, su01Department?] */
  accounts: [string, string, string, string, string, string, number, number, string[], string[], Record<string, string>, string, string, string?, string?][];
}

const TYPE_OF_JOB: Record<string, PersonType> = {
  LAGER: "lager", KOMM: "lager", SCHICHT: "lager", DISPO: "lager", QS: "lager",
  BASIS: "it", AUTH: "it", APPBETR: "it", DEV: "entwickler", EXTERN: "extern",
};
const CLUSTER_OF: Record<string, LeverCluster> = { FI_VOLL: "Büro", BELEG_VOLL: "Büro", WF_SONDER: "Externe", RECH_FREIGABE: "Einkauf", FIGM: "Büro", USERADM: "IT", KNA1_PFLEGE: "Büro", LFA1_PFLEGE: "Einkauf" };
const RATIONALE_OF: Record<string, string> = {
  FI_VOLL: "F_BKPF_BUK ACTVT 22/43 (Advanced) — dasselbe Objekt liegt als 06/07 nur auf Core: der Wert entscheidet, nicht das Objekt.",
  BELEG_VOLL: "F_BKPF_BLA 22 / F_BKPF_KOA 43 (Advanced) — Belegvollzugriff, den Sachbearbeitung selten braucht.",
  WF_SONDER: "S_WFFLXDEF 06/60 (Advanced, Workflow-Definition) — Externe sollten Workflows auslösen, nicht definieren.",
  RECH_FREIGABE: "M_RECH_BUK 01/81 (Advanced) in Rollen, die überwiegend Anzeige brauchen.",
  FIGM: "F_FIGM_GNT 01/G1 (Advanced) — Fördermittelverwaltung, historisch in Sammelrollen mitgeschleppt.",
  USERADM: "S_USER_GRP 01/22 (Advanced) — Benutzeranlage in IT-Rollen, die eigentlich Monitoring abdecken.",
  KNA1_PFLEGE: "F_KNA1_APP 01/02 (Core) — Kundenstammpflege hebt Anzeige-Nutzer auf Core.",
  LFA1_PFLEGE: "F_LFA1_APP 05 / F_LFA1_BUK 06 (Core) — Lieferantenpflege hebt Anzeige-Nutzer auf Core.",
};
const TRACE_OF_CODE: Record<string, TraceState> = { g: "genutzt", n: "nie-genutzt", k: "kein-trace" };

/** Synchron: JSON → Dataset. Wird vom Fetch-Pfad und von Tests (fs) genutzt. */
export function datasetFromSeedJson(j: SeedJson): Dataset {
  const lidOfSid = new Map<string, string>();
  for (const l of assignedLandscapes) for (const s of l.systems) lidOfSid.set(s, l.id);
  for (const s of j.systems) if (!lidOfSid.has(s.sid)) lidOfSid.set(s.sid, s.landscape);
  const unitOfLid = new Map(assignedLandscapes.map((l) => [l.id, l.unit]));

  // Rollen — Klasse aus dem Regelwerk, hier in der App.
  const singleRoles: SingleRole[] = j.singleRoles.map((r) => {
    const authValues = r.values.map(([obj, field, val]) => ({ obj, field, val }));
    const c = classifyValues(authValues);
    return { id: r.id, name: r.name, landscapeId: lidOfSid.get(r.sid) ?? "L11", sid: r.sid, template: r.template, objectsTotal: r.objectsTotal, authValues, forcedClass: c.cls, forcedStep: c.step, engines: c.engines, removable: r.removable, leverId: r.removable ? `H-${r.id}` : undefined };
  });
  const compositeRoles: CompositeRole[] = j.compositeRoles.map((c) => ({ id: c.id, name: c.name, landscapeId: lidOfSid.get(c.sid) ?? "L11", sid: c.sid, template: c.template, singleRoleIds: c.singles }));

  // Personen-Grundgerüst
  const persons: Person[] = [];
  const personById = new Map<string, Person>();
  for (const [pernr, job, unit, first, last, email, department, location] of j.persons) {
    const p: Person = { id: `P-${pernr}`, type: TYPE_OF_JOB[job] ?? "buero", unit, name: `${first} ${last}`.trim(), email: email || undefined, department, location, accountIds: [], landscapeIds: [], primaryLandscape: "", licenseClass: "NotRelevant", cleanClass: "NotRelevant", fue: 0, drivers: [], leverIds: [] };
    persons.push(p); personById.set(p.id, p);
  }

  // Konten (fachliche und technische) + Trace je Konto und Einzelrolle
  const accounts: Account[] = [];
  const roleTrace = new Map<string, TraceState>();
  let technicalCount = 0, seq = 0;
  for (const [pernr, sid, mandt, bname, email, lastLogon, locked, dev, composites, singles, traces, licType, , su01Function, su01Department] of j.accounts) {
    const id = "USER-" + String(seq++).padStart(6, "0");
    const personId = pernr ? `P-${pernr}` : `T-${bname}`;
    const codes = Object.values(traces);
    const accTrace: TraceState = codes.includes("g") ? "genutzt" : codes.includes("n") ? "nie-genutzt" : "kein-trace";
    accounts.push({ id, personId, sid, client: mandt, username: bname, personalnummer: pernr || undefined, email: email || undefined, lastLogon: lastLogon || undefined, locked: locked === 1, licTypeMaintained: licType || undefined, compositeRoleIds: composites, singleRoleIds: singles, isDev: dev === 1 || undefined, accountClass: "NotRelevant", accountStep: 9999, engines: [], trace: accTrace, su01Function: su01Function || undefined, su01Department: su01Department || undefined });
    for (const [rid, code] of Object.entries(traces)) roleTrace.set(`${id}|${rid}`, TRACE_OF_CODE[code] ?? "kein-trace");
    const p = personById.get(personId);
    if (!p) { technicalCount++; continue; }
    p.accountIds.push(id);
    const lid = lidOfSid.get(sid) ?? "L11";
    if (!p.landscapeIds.includes(lid)) p.landscapeIds.push(lid);
    if (!p.primaryLandscape) { p.primaryLandscape = lid; p.unit = unitOfLid.get(lid) ?? p.unit; }
  }
  for (const p of persons) if (!p.primaryLandscape) p.primaryLandscape = "L11";

  const built = buildDataset({ persons, accounts, singleRoles, compositeRoles, roleTrace, clusterOf: (t) => CLUSTER_OF[t], rationaleOf: (t) => RATIONALE_OF[t] });
  let heute = 0; for (const p of built.persons) heute += weight(p.licenseClass, DEFAULT_RATIO);
  return {
    persons: built.persons, accounts: built.accounts, roles: built.roles, singleRoles, compositeRoles, levers: built.levers,
    entitlements: baseEntitlements(), snapshots: snapshotsFor(Math.round(heute)), landscapes,
    technicalCount, meta: { generatedFrom: j.meta.seed, accountsTotal: accounts.length, pceVersion: "V1.70", source: "seed", generatedAt: j.meta.generatedAt },
  };
}

/** Lädt die Seed-Datei relativ zur App (funktioniert unter jedem Basispfad). */
export async function loadSeedDataset(url = "seed/dataset.json"): Promise<Dataset> {
  // no-cache = immer beim Server nachfragen (304 wenn unverändert). Mit force-cache blieb nach
  // einem neuen Seed die alte Datei im Browser hängen — die App zeigte veraltete Zahlen.
  const res = await fetch(url, { cache: "no-cache" });
  if (!res.ok) throw new Error(`Seed nicht ladbar: ${res.status}`);
  const json = (await res.json()) as SeedJson;
  return datasetFromSeedJson(json);
}
